import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ROUTES } from "@/routes";
import { EventsRepository } from "@/application/events/events_repository";
import type { BusinessSummary } from "@/application/events/types";

const repo = new EventsRepository();

interface AccountLinkProps {
  account: string | null;
}

/**
 * Renders an account ID.
 * - If null: shows "—"
 * - If resolved to a merchant: shows a link to the merchant detail page
 * - If unresolved (no match or network failure): shows raw acct_* text
 * Resolution is best-effort and silent on failure.
 */
export function AccountLink({ account }: AccountLinkProps) {
  const [merchant, setMerchant] = useState<BusinessSummary | null>(null);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    // Reset when account changes so we never render a previous account's
    // merchant data for a different (or unresolved) acct_* value.
    setMerchant(null);
    setResolved(false);

    if (!account) {
      setResolved(true);
      return;
    }

    let cancelled = false;
    repo
      .resolveAccountToMerchant(account)
      .then((result) => {
        if (!cancelled) {
          setMerchant(result);
          setResolved(true);
        }
      })
      .catch(() => {
        // Silent degradation
        if (!cancelled) setResolved(true);
      });

    return () => {
      cancelled = true;
    };
  }, [account]);

  if (!account) {
    return <span className="text-subtle-foreground">—</span>;
  }

  if (!resolved) {
    return (
      <span className="text-muted-foreground font-mono text-xs">{account}</span>
    );
  }

  if (merchant) {
    return (
      <span className="flex flex-col gap-0.5">
        <Link
          to={ROUTES.MERCHANT_DETAIL.replace(":businessId", merchant.id)}
          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
        >
          {merchant.businessName}
        </Link>
        <span className="text-subtle-foreground font-mono text-xs">
          {account}
        </span>
      </span>
    );
  }

  return (
    <span className="text-muted-foreground font-mono text-xs">{account}</span>
  );
}
